import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  FiCheck,
  FiBarChart2,
  FiZap,
  FiAlertTriangle,
  FiSettings,
  FiPlus,
  FiUser,
  FiSlash,
  FiEdit3,
  FiRefreshCw,
  FiFileText,
  FiTrash2,
  FiX,
  FiShare2,
} from 'react-icons/fi';
import { ROUTES } from '../../app/routes';
import { useCommandCenterStats } from '../../context/CommandCenterStatsContext';
import { listDwAgents } from '../claudeagents/claudeAgentApi';
import { fetchKanbanCustomCategoriesRaw } from '../../services/kanbanAIService';
import { forgetShared } from '../../utils/inFlight';
import CategoryShareMenuModal from '../../components/CategoryShareMenuModal';

type KanbanTaskMode = 'auto' | 'manual' | 'disabled';

type KanbanTaskCategory = {
  id: number;
  name: string;
  title: string;
  description: string;
  // Custom (user-created) categories carry their labels/prompts directly on
  // their own row (kanban_custom_sop_category), unlike built-in categories
  // whose SOP config lives in the separate per-user overlay table.
  isCustom?: boolean;
  triggerLabel?: string;
  completionLabel?: string;
  identificationPrompt?: string;
  executionPrompt?: string;
  // 'label' — the AI triggers on a specific inbox label (Trigger/Completion label).
  // 'all' — the AI evaluates every task via the Identification Prompt instead.
  matchMode?: 'label' | 'all';
  assigneeType?: number;
  assigneeAgentName?: string;
  assigneeAgentId?: string;
};

const SOP_ASSIGNEE_TYPES: { value: number; label: string }[] = [
  { value: 0, label: 'User' },
];

// SOP config responses aren't consistent about assignee-agent key casing
// (assigneeAgentName vs agentName vs agent_name vs nested agent.name) — same
// defensive read Command Center uses (see readAgentName in MomentumCommandPage).
const normalizeSopAgentName = (value: unknown): string => {
  const name = typeof value === 'string' ? value.trim() : '';
  if (!name) return '';
  const lowered = name.toLowerCase();
  if (lowered === 'null' || lowered === 'undefined') return '';
  return name;
};

const readSopAgentName = (row: any): string =>
  normalizeSopAgentName(
    row?.assigneeAgentName ?? row?.agentName ?? row?.agent_name ?? row?.AgentName ?? row?.agent?.name,
  );

const readSopAgentId = (row: any): string => {
  const id = row?.assigneeAgentId ?? row?.agentId ?? row?.agent_id ?? row?.AgentId ?? row?.agent?.id;
  if (typeof id === 'string') return id.trim();
  return id != null ? String(id) : '';
};

const KANBAN_TYPE_META: Record<
  string,
  { color: string; icon: React.ElementType }
> = {
  COI_REQUEST: { color: '#2A65F2', icon: FiFileText },
  NOTICE_OF_CANCELLATION: { color: '#ef4444', icon: FiAlertTriangle },
  POLICY_CHANGE: { color: '#f97316', icon: FiSettings },
  PAYMENT_REMINDER: { color: '#f59e0b', icon: FiBarChart2 },
  RENEWAL_NOTIFICATION: { color: '#22c55e', icon: FiRefreshCw },
  SERVICE_REQUEST: { color: '#8b5cf6', icon: FiPlus },
  OTHER: { color: '#6b7280', icon: FiZap },
};

// ─────────────────────────────────────────────────────────────────────────────

/* Visually hidden but announced (inline so it holds wherever this renders). */
const SR_ONLY: React.CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};
// Command Center configuration panel — right-sidebar content shown while on
// the Command Center / Kanban page. Two tabs: an Analytics dashboard and a
// task-routing Configuration screen (SOP category list + create/edit modals).
// Extracted from SidebarRight.tsx, which renders it with no props whenever
// `showKanbanConfig` is true — self-contained via its own context hooks,
// following the same pattern as RateQuoteLinkSidebarPanel / MacWorkflowsSidebarContent.
// ─────────────────────────────────────────────────────────────────────────────
// Command Center categories are configured per channel. Email categories trigger on
// a label or a prompt (Composio uses the prompt); Voice and SMS route by AI
// identification prompt only.
type CategoryChannel = 1 | 2 | 3;
const CHANNEL_EMAIL: CategoryChannel = 1;
const CATEGORY_CHANNELS: { id: CategoryChannel; label: string; hint: string }[] = [
  { id: 1, label: 'Email', hint: 'An email carrying a category\'s trigger label becomes its task.' },
  { id: 2, label: 'Voice', hint: 'The AI matches each call summary to a category by its identification prompt.' },
  { id: 3, label: 'SMS', hint: 'The AI matches each text message to a category by its identification prompt.' },
];
const channelStorageKey = (base: string, channel: CategoryChannel) =>
  channel === CHANNEL_EMAIL ? base : `${base}_${channel}`;
function readChannelCache<T>(base: string, channel: CategoryChannel): Record<string, T> {
  try {
    const saved = localStorage.getItem(channelStorageKey(base, channel));
    if (saved) return JSON.parse(saved);
  } catch {
    /* ignore */
  }
  return {};
}

const CommandCenterConfigSidebarPanel: React.FC = () => {
  const navigate = useNavigate();
  const [kanbanSidebarTab, setKanbanSidebarTab] = useState<'dashboard' | 'config'>('dashboard');
  // The Configuration tab's data (process label, categories, configurations, SOP
  // configs, custom-category modes) loads the first time that tab is opened — the
  // panel opens on Analytics, which needs none of it. Stays true afterwards, so
  // switching tabs back and forth doesn't refetch.
  const [configDataRequested, setConfigDataRequested] = useState(false);
  useEffect(() => {
    if (kanbanSidebarTab === 'config') setConfigDataRequested(true);
  }, [kanbanSidebarTab]);
  const { stats: ccStats } = useCommandCenterStats();
  const [kanbanTaskTypes, setKanbanTaskTypes] = useState<KanbanTaskCategory[]>(
    [],
  );
  const [categoryChannel, setCategoryChannel] = useState<CategoryChannel>(CHANNEL_EMAIL);
  const [loadedChannel, setLoadedChannel] = useState<CategoryChannel | null>(null);
  const kanbanTypesLoaded = loadedChannel === categoryChannel;
  const channelMatchMode: 'label' | 'all' = categoryChannel === CHANNEL_EMAIL ? 'label' : 'all';
  const [defaultCategoryPrompts, setDefaultCategoryPrompts] = useState<Record<string, string>>({});
  const [kanbanTypeConfig, setKanbanTypeConfig] = useState<
    Record<string, KanbanTaskMode>
  >(() => {
    try {
      const saved = localStorage.getItem('kanban_type_config');
      if (saved) return JSON.parse(saved);
    } catch {
      /* ignore */
    }
    return {};
  });
  const KANBAN_MODE_INT: Record<KanbanTaskMode, number> = {
    auto: 0,
    manual: 1,
    disabled: 2,
  };
  // Custom categories store Mode on their own row in MyAMP: the built-in table is keyed
  // by category id alone and the two id spaces overlap, so they cannot share it.
  const setKanbanTypeMode = useCallback(
    (key: string, catId: number, mode: KanbanTaskMode, isCustom: boolean) => {
      setKanbanTypeConfig((prev) => {
        const next = { ...prev, [key]: mode };
        localStorage.setItem(channelStorageKey('kanban_type_config', categoryChannel), JSON.stringify(next));
        return next;
      });
      const authToken = localStorage.getItem('authToken') || '';
      const url = isCustom
        ? `${process.env.REACT_APP_MyAMP_Url}/api/command-center/custom-category-mode`
        : `${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumKanban/task/category/configuration/upsert`;
      const body = isCustom
        ? { category_id: catId, mode: KANBAN_MODE_INT[mode] }
        : { kanbanTaskCategoryId: catId, mode: KANBAN_MODE_INT[mode], channel: categoryChannel };
      fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }).catch(() => {});
    },
    // KANBAN_MODE_INT is a constant lookup recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categoryChannel],
  );

  // ── Command Center "process label" ──
  // Only emails carrying this Gmail label are turned into Kanban tasks by the
  // incoming-email webhook (empty = process all). Persisted server-side via MyAMP
  // /api/command-center/process-label (KanbanCommandCenterConfig.ProcessLabel).
  const [processLabel, setProcessLabel] = useState('');
  const [processLabelInput, setProcessLabelInput] = useState('');
  const [processLabelLoaded, setProcessLabelLoaded] = useState(false);
  const [processLabelSaving, setProcessLabelSaving] = useState(false);

  useEffect(() => {
    if (!configDataRequested) return;
    let cancelled = false;
    const authToken = localStorage.getItem('authToken') || '';
    if (!authToken) return;
    fetch(
      `${process.env.REACT_APP_MyAMP_Url}/api/command-center/process-label`,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        const lbl = String(d.process_label || '');
        setProcessLabel(lbl);
        setProcessLabelInput(lbl);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setProcessLabelLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [configDataRequested]);

  const saveProcessLabel = useCallback(() => {
    const authToken = localStorage.getItem('authToken') || '';
    const value = processLabelInput.trim();
    setProcessLabelSaving(true);
    fetch(
      `${process.env.REACT_APP_MyAMP_Url}/api/command-center/process-label`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ process_label: value }),
      },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) setProcessLabel(String(d.process_label || ''));
      })
      .catch(() => {})
      .finally(() => setProcessLabelSaving(false));
  }, [processLabelInput]);

  // ── Custom (user-created) SOP categories — persisted server-side (per user)
  // via /api/MomentumKanban/task/category/custom, and merged into
  // kanbanTaskTypes so they render through the same list/mode-toggle/edit UI
  // as built-in categories (see the fetch effect and handleCreateSopCategory
  // below). Unlike built-ins, a custom category's labels/prompts live directly
  // on its own row — there's no separate overlay table for it. ──
  const [showNewSopCategoryModal, setShowNewSopCategoryModal] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [newCatTrigger, setNewCatTrigger] = useState('');
  const [newCatCompletion, setNewCatCompletion] = useState('');
  const [newCatIdentificationPrompt, setNewCatIdentificationPrompt] = useState('');
  const [newCatExecutionPrompt, setNewCatExecutionPrompt] = useState('');
  const [newCatMatchMode, setNewCatMatchMode] = useState<'label' | 'all'>('label');
  const [newCatAssigneeType, setNewCatAssigneeType] = useState(0);
  const [newCatAssigneeAgentName, setNewCatAssigneeAgentName] = useState('');
  const [newCatAssigneeAgentId, setNewCatAssigneeAgentId] = useState('');
  const [creatingSopCategory, setCreatingSopCategory] = useState(false);
  const [createSopError, setCreateSopError] = useState('');
  const [askAiNewExecutionPrompt, setAskAiNewExecutionPrompt] = useState('');
  const [askAiNewExecutionLoading, setAskAiNewExecutionLoading] = useState(false);
  const [askAiNewExecutionError, setAskAiNewExecutionError] = useState('');
  const [askAiNewExecutionIsUpdate, setAskAiNewExecutionIsUpdate] = useState(false);

  // Digital Workforce agents, shared by the new-category and edit-SOP assignee
  // dropdowns — same source/cache pattern as CommandIssuePanel's assignee picker.
  const [sopDwAgents, setSopDwAgents] = useState<{ id: string; name: string; isPrimary: boolean }[]>([]);
  const sortByPrimary = (list: { id: string; name: string; isPrimary: boolean }[]) =>
    [...list].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
  useEffect(() => {
    try {
      const s = localStorage.getItem('momentum_digital_workforce_agents');
      if (s) {
        const raw = JSON.parse(s) as any[];
        const cached = raw
          .filter(a => typeof a?.id === 'string' && typeof a?.name === 'string' && a.name.trim())
          .map(a => ({ id: a.id as string, name: (a.name as string).trim(), isPrimary: !!a.isPrimary }));
        if (cached.length > 0) setSopDwAgents(sortByPrimary(cached));
      }
    } catch {}
    listDwAgents(false, true)
      .then(records => {
        const active = records.filter(r => !r.is_archived);
        setSopDwAgents(sortByPrimary(active.map(r => ({ id: r.agent_id, name: r.name, isPrimary: !!r.is_primary_agent }))));
      })
      .catch(() => {});
  }, []);

  const closeNewSopCategoryModal = useCallback(() => {
    setShowNewSopCategoryModal(false);
    setNewCatName('');
    setNewCatTrigger('');
    setNewCatCompletion('');
    setNewCatIdentificationPrompt('');
    setNewCatExecutionPrompt('');
    setNewCatMatchMode('label');
    setNewCatAssigneeType(0);
    setNewCatAssigneeAgentName('');
    setNewCatAssigneeAgentId('');
    setCreateSopError('');
    setAskAiNewExecutionPrompt('');
    setAskAiNewExecutionError('');
  }, []);

  // New-category default assignee: the Primary DW agent if one exists, else
  // User (the form's initial state already is User, so the "no primary"
  // branch is simply doing nothing). sopDwAgents can still be loading when
  // the modal opens, so this re-checks as the list arrives -- but only until
  // it either finds a primary agent or the user picks an assignee manually,
  // so it never stomps a deliberate choice made in the meantime.
  const newCatAutoAssigneeAppliedRef = useRef(false);
  useEffect(() => {
    if (!showNewSopCategoryModal) {
      newCatAutoAssigneeAppliedRef.current = false;
      return;
    }
    if (newCatAutoAssigneeAppliedRef.current) return;
    if (newCatAssigneeAgentId || newCatAssigneeType !== 0) {
      newCatAutoAssigneeAppliedRef.current = true;
      return;
    }
    const primary = sopDwAgents.find(a => a.isPrimary);
    if (!primary) return;
    newCatAutoAssigneeAppliedRef.current = true;
    setNewCatAssigneeType(2);
    setNewCatAssigneeAgentId(primary.id);
    setNewCatAssigneeAgentName(primary.name);
  }, [showNewSopCategoryModal, sopDwAgents, newCatAssigneeAgentId, newCatAssigneeType]);

  useEffect(() => {
    setNewCatMatchMode(channelMatchMode);
  }, [channelMatchMode, showNewSopCategoryModal]);

  const handleCreateSopCategory = useCallback(() => {
    const name = newCatName.trim();
    if (!name || creatingSopCategory) return;
    const triggerLabel = newCatTrigger.trim();
    const completionLabel = newCatCompletion.trim();
    const identificationPrompt = newCatIdentificationPrompt.trim();
    const executionPrompt = newCatExecutionPrompt.trim();
    const categoryMatchType = newCatMatchMode === 'label' ? 0 : 1;
    const assigneeType = newCatAssigneeType;
    const agentName = assigneeType === 2 ? newCatAssigneeAgentName.trim() : '';
    const agentId = assigneeType === 2 ? newCatAssigneeAgentId.trim() : '';
    // Trigger on = All has no label to select the category, so the prompt is the only
    // criteria classify-email gets — saved blank it competes on its name alone and pulls
    // unrelated emails in. Required for custom categories only (this modal creates just
    // those); built-in types are already known to the classifier.
    if (newCatMatchMode === 'label' && !triggerLabel) {
      setCreateSopError('Trigger label is required when Trigger on is Label.');
      return;
    }
    if (newCatMatchMode === 'all' && !identificationPrompt) {
      setCreateSopError(categoryChannel === CHANNEL_EMAIL
        ? 'Identification Prompt is required when Trigger on is Prompt.'
        : 'Identification Prompt is required for Voice and SMS categories.');
      return;
    }
    setCreateSopError('');
    setCreatingSopCategory(true);
    const authToken = localStorage.getItem('authToken') || '';
    const payload: any = {
      name,
      categoryMatchType,
      assigneeType,
      channel: categoryChannel,
    };
    // Only the Trigger label / Identification Prompt pair swaps with the
    // match mode in the UI — send both on every save with the inactive one
    // cleared so switching modes doesn't leave stale values on the server.
    // Completion label and Execution Prompt are shown in BOTH modes, so they
    // are always sent as typed.
    payload.triggerLabel = newCatMatchMode === 'label' ? triggerLabel : '';
    payload.completionLabel = completionLabel;
    payload.identificationPrompt = newCatMatchMode === 'all' ? identificationPrompt : '';
    payload.executionPrompt = executionPrompt;
    // Always include agent fields: populated when assigneeType is 2, empty otherwise
    payload.agentName = assigneeType === 2 ? agentName : '';
    payload.agentId = assigneeType === 2 ? agentId : '';
    forgetShared('kanbanCustomCategories|'); // custom categories change — don't reuse the cached list
    fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumKanban/task/category/custom/upsert`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((json) => {
        const newId = json?.id;
        if (!newId) return;
        setKanbanTaskTypes((prev) => [
          ...prev,
          {
            id: newId,
            name,
            title: name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'CUSTOM',
            description: '',
            isCustom: true,
            triggerLabel: payload.triggerLabel,
            completionLabel: payload.completionLabel,
            identificationPrompt: payload.identificationPrompt,
            executionPrompt: payload.executionPrompt,
            matchMode: newCatMatchMode,
            assigneeType,
            assigneeAgentName: agentName,
            assigneeAgentId: agentId,
          },
        ]);
        closeNewSopCategoryModal();
      })
      .catch(() => {})
      .finally(() => setCreatingSopCategory(false));
  }, [
    newCatName,
    newCatTrigger,
    newCatCompletion,
    newCatIdentificationPrompt,
    newCatExecutionPrompt,
    newCatMatchMode,
    newCatAssigneeType,
    newCatAssigneeAgentName,
    newCatAssigneeAgentId,
    creatingSopCategory,
    closeNewSopCategoryModal,
    categoryChannel,
  ]);

  // ── Delete a custom category (soft delete server-side: the row is flagged,
  // not removed, so tasks already filed under it keep a resolvable id).
  const [deletingSopCategory, setDeletingSopCategory] =
    useState<KanbanTaskCategory | null>(null);
  const [deleteSopBusy, setDeleteSopBusy] = useState(false);
  const [deleteSopError, setDeleteSopError] = useState('');

  const closeDeleteSopModal = useCallback(() => {
    if (deleteSopBusy) return;
    setDeletingSopCategory(null);
    setDeleteSopError('');
  }, [deleteSopBusy]);

  const handleDeleteSopCategory = useCallback(() => {
    const cat = deletingSopCategory;
    if (!cat || !cat.isCustom || deleteSopBusy) return;
    setDeleteSopBusy(true);
    setDeleteSopError('');
    const authToken = localStorage.getItem('authToken') || '';
    forgetShared('kanbanCustomCategories|'); // custom categories change — don't reuse the cached list
    fetch(
      `${(process.env.REACT_APP_Auth_Url)}/api/MomentumKanban/task/category/custom/${cat.id}`,
      { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } },
    )
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((json) => {
        if (json?.success === false) throw new Error();
        setKanbanTaskTypes((prev) => prev.filter((c) => !(c.isCustom && c.id === cat.id)));
        setKanbanTypeConfig((prev) => {
          const next = { ...prev };
          delete next[cat.title];
          localStorage.setItem(channelStorageKey('kanban_type_config', categoryChannel), JSON.stringify(next));
          return next;
        });
        // Also park the category as Disabled in MyAMP, which holds custom-category
        // Mode on its own row, so inbox routing stops selecting it even where it
        // reads categories without the deleted flag. Best-effort.
        fetch(`${process.env.REACT_APP_MyAMP_Url}/api/command-center/custom-category-mode`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ category_id: cat.id, mode: KANBAN_MODE_INT.disabled }),
        }).catch(() => {});
        setDeletingSopCategory(null);
      })
      .catch(() => setDeleteSopError('Could not delete this category. Please try again.'))
      .finally(() => setDeleteSopBusy(false));
    // KANBAN_MODE_INT is a constant lookup recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletingSopCategory, deleteSopBusy, categoryChannel]);

  // ── Share a custom category (magic link; see CategoryShareMenuModal). Only
  // custom categories are shareable — built-ins are platform templates every
  // agency already has, the same rule Digital Workforce applies to its
  // default agents.
  const [sharingSopCategory, setSharingSopCategory] =
    useState<KanbanTaskCategory | null>(null);

  // Create is available once the category has a name and — on Trigger on = All — a
  // prompt, so the button reflects the rule instead of only failing on click.
  const newCatCanSave =
    !!newCatName.trim() &&
    (newCatMatchMode !== 'label' || !!newCatTrigger.trim()) &&
    (newCatMatchMode !== 'all' || !!newCatIdentificationPrompt.trim());

  // ── Per-category SOP config (trigger/completion label + identification/
  // execution prompt) for BUILT-IN categories — persisted server-side (per
  // user) via /api/MomentumKanban/task/category/sop-config, mirrored to
  // localStorage so it's available immediately on next load. ──
  type SopCategoryConfig = {
    triggerLabel: string;
    completionLabel: string;
    identificationPrompt: string;
    executionPrompt: string;
    matchMode?: 'label' | 'all';
    assigneeType?: number;
    assigneeAgentName?: string;
    assigneeAgentId?: string;
  };
  const [sopConfigByCategory, setSopConfigByCategory] = useState<
    Record<string, SopCategoryConfig>
  >(() => {
    try {
      const saved = localStorage.getItem('kanban_sop_category_config');
      if (saved) return JSON.parse(saved);
    } catch {
      /* ignore */
    }
    return {};
  });
  const [editingSopCategory, setEditingSopCategory] =
    useState<KanbanTaskCategory | null>(null);
  const [editTrigger, setEditTrigger] = useState('');
  const [editCompletion, setEditCompletion] = useState('');
  const [editIdentificationPrompt, setEditIdentificationPrompt] = useState('');
  const [editExecutionPrompt, setEditExecutionPrompt] = useState('');
  const [editMatchMode, setEditMatchMode] = useState<'label' | 'all'>('label');
  const [editAssigneeType, setEditAssigneeType] = useState(0);
  const [editAssigneeAgentName, setEditAssigneeAgentName] = useState('');
  const [editAssigneeAgentId, setEditAssigneeAgentId] = useState('');

  // Same re-sync pattern as CommandIssuePanel's assignee effect: the edit-SOP
  // modal can open before sopDwAgents finishes loading, so re-resolve the
  // agent id by name once the list lands instead of leaving it unresolved
  // for the rest of the modal's life.
  useEffect(() => {
    if (!editingSopCategory) return;
    const name = editAssigneeAgentName.trim();
    if (!name) return;
    const match = sopDwAgents.find(a => a.name.trim().toLowerCase() === name.toLowerCase());
    if (match && match.id !== editAssigneeAgentId) {
      setEditAssigneeAgentId(match.id);
    }
  }, [sopDwAgents, editingSopCategory, editAssigneeAgentName, editAssigneeAgentId]);

  const [savingSopCategory, setSavingSopCategory] = useState(false);
  const [saveSopError, setSaveSopError] = useState('');
  const [askAiIdentificationPrompt, setAskAiIdentificationPrompt] =
    useState('');
  const [askAiIdentificationLoading, setAskAiIdentificationLoading] =
    useState(false);
  const [askAiIdentificationError, setAskAiIdentificationError] =
    useState('');
  const [askAiExecutionPrompt, setAskAiExecutionPrompt] = useState('');
  const [askAiExecutionLoading, setAskAiExecutionLoading] = useState(false);
  const [askAiExecutionError, setAskAiExecutionError] = useState('');
  const [askAiExecutionIsUpdate, setAskAiExecutionIsUpdate] = useState(false);

  const openEditSopModal = useCallback(
    (cat: KanbanTaskCategory) => {
      if (cat.isCustom) {
        setEditTrigger(cat.triggerLabel ?? '');
        setEditCompletion(cat.completionLabel ?? '');
        setEditIdentificationPrompt(cat.identificationPrompt ?? '');
        setEditExecutionPrompt(cat.executionPrompt ?? '');
        setEditMatchMode(categoryChannel === CHANNEL_EMAIL ? (cat.matchMode ?? 'label') : 'all');
        const agentName = cat.assigneeAgentName ?? '';
        const agentId = cat.assigneeAgentId ?? '';
        // Agent name is the source of truth — an agent that exists on the
        // record always wins over assigneeType, regardless of whether the ID
        // round-tripped or the Digital Workforce list has loaded yet. The
        // select itself resolves the ID (directly, by name, or via a
        // synthetic fallback option) at render time.
        if (agentName) {
          const agent = sopDwAgents.find(a => a.name.trim().toLowerCase() === agentName.trim().toLowerCase());
          setEditAssigneeType(2);
          setEditAssigneeAgentName(agentName);
          setEditAssigneeAgentId(agentId || agent?.id || '');
        } else {
          setEditAssigneeType(cat.assigneeType ?? 0);
          setEditAssigneeAgentName('');
          setEditAssigneeAgentId('');
        }
      } else {
        const existing = sopConfigByCategory[cat.title];
        setEditTrigger(existing?.triggerLabel ?? '');
        setEditCompletion(existing?.completionLabel ?? '');
        setEditIdentificationPrompt(existing?.identificationPrompt ?? '');
        setEditExecutionPrompt(existing?.executionPrompt ?? '');
        setEditMatchMode(categoryChannel === CHANNEL_EMAIL ? (existing?.matchMode ?? 'label') : 'all');
        const agentName = existing?.assigneeAgentName ?? '';
        const agentId = existing?.assigneeAgentId ?? '';
        if (agentName) {
          const agent = sopDwAgents.find(a => a.name.trim().toLowerCase() === agentName.trim().toLowerCase());
          setEditAssigneeType(2);
          setEditAssigneeAgentName(agentName);
          setEditAssigneeAgentId(agentId || agent?.id || '');
        } else {
          setEditAssigneeType(existing?.assigneeType ?? 0);
          setEditAssigneeAgentName('');
          setEditAssigneeAgentId('');
        }
      }
      setAskAiIdentificationPrompt('');
      setAskAiIdentificationError('');
      setAskAiExecutionPrompt('');
      setAskAiExecutionError('');
      setSaveSopError('');
      setEditingSopCategory(cat);
    },
    [sopConfigByCategory, sopDwAgents, categoryChannel],
  );

  const closeEditSopModal = useCallback(() => {
    setEditingSopCategory(null);
    setSaveSopError('');
    setAskAiIdentificationPrompt('');
    setAskAiIdentificationError('');
    setAskAiExecutionPrompt('');
    setAskAiExecutionError('');
    setEditAssigneeAgentId('');
  }, []);

  // Shared caller for the "ask AI" mini-boxes under each prompt textarea —
  // each box asks independently and only updates its own field. The endpoint
  // streams its answer as SSE `data:` events shaped {"response": "<chunk>"}
  // (same shape as the momentum-kanban-create stream) — `applyTo` is called
  // with the accumulated text after every chunk so the textarea fills in
  // live instead of waiting for the whole response. `categoryName` is
  // resolved by the caller — edit passes the built-in enum key (or 'OTHER'
  // for custom), create always passes 'OTHER' (the category doesn't exist
  // server-side yet) and `categoryLabel` carries the human name instead.
  //
  // `extra.existingPrompt` is the textarea's current text. It is sent
  // whenever the caller passes one (even ''): its presence tells the server
  // the editor is authoritative — non-empty = UPDATE this draft in place
  // (same contract as the Routines builder's existing_instruction), empty =
  // write a new prompt. Callers that omit it keep the server's legacy
  // saved-prompt base.
  //
  // `extra.isCustom` matters because custom categories are sent as 'OTHER':
  // a custom category's Execution Prompt is its COMPLETE SOP, while a
  // built-in category's (the built-in OTHER included) is only added on top
  // of the built-in steps at run time, so the server writes just those
  // additions instead of a copy of the SOP that would then run twice.
  const callAskAiForPrompt = useCallback(
    (
      askText: string,
      applyTo: (value: string) => void,
      categoryName: string,
      extra?: {
        existingPrompt?: string;
        categoryLabel?: string;
        isCustom?: boolean;
      },
    ) => {
      const authToken = localStorage.getItem('authToken') || '';
      const ncToken = localStorage.getItem('amsToken') || '';
      const payload: Record<string, string | boolean | number> = {
        category_name: categoryName,
        prompt: askText,
        nc_token: ncToken,
        channel: categoryChannel,
      };
      if (extra?.existingPrompt !== undefined) {
        payload.existing_prompt = extra.existingPrompt;
      }
      if (extra?.categoryLabel) payload.category_label = extra.categoryLabel;
      if (extra?.isCustom) payload.is_custom = true;
      return fetch(
        `${process.env.REACT_APP_MyAMP_Url}/api/ams/momentum-kanban-category-prompt-modify`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      ).then(async (res) => {
        if (!res.ok || !res.body) {
          let msg = `Request failed (${res.status})`;
          try {
            const json = await res.json();
            msg = json?.errorMessage || json?.message || msg;
          } catch {
            /* keep fallback message */
          }
          throw new Error(msg);
        }

        let streamText = '';
        const consumeEvent = (event: string) => {
          const dataLine = event
            .split(/\r?\n/)
            .map((l) => l.trim())
            .find((l) => l.startsWith('data:'));
          if (!dataLine) return;
          let json: any = null;
          try {
            json = JSON.parse(dataLine.replace(/^data:\s*/, ''));
          } catch {
            return;
          }
          if (json?.errorMessage) throw new Error(json.errorMessage);
          const chunk =
            json?.response ?? json?.prompt ?? json?.result ?? json?.text;
          if (typeof chunk === 'string' && chunk) {
            streamText += chunk;
            applyTo(streamText);
          }
        };

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let sseBuffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          sseBuffer += decoder.decode(value, { stream: true });
          const events = sseBuffer.split('\n\n');
          sseBuffer = events.pop() ?? '';
          for (const event of events) consumeEvent(event);
        }
        if (sseBuffer.trim()) consumeEvent(sseBuffer);

        if (!streamText) throw new Error('Empty response');
      });
    },
    [categoryChannel],
  );

  const handleAskAiForIdentificationPrompt = useCallback(() => {
    if (!editingSopCategory || askAiIdentificationLoading) return;
    const trimmedAsk = askAiIdentificationPrompt.trim();
    if (!trimmedAsk) return;
    // The endpoint only accepts the built-in enum keys (COI_REQUEST,
    // SERVICE_REQUEST, …) held in `title` — `name` is the display label and
    // custom-category titles are user-generated slugs, so both would 400
    // with "Unknown category".
    const categoryName = editingSopCategory.isCustom
      ? 'OTHER'
      : editingSopCategory.title;
    setAskAiIdentificationLoading(true);
    setAskAiIdentificationError('');
    setEditIdentificationPrompt('');
    callAskAiForPrompt(trimmedAsk, setEditIdentificationPrompt, categoryName)
      .then(() => setAskAiIdentificationPrompt(''))
      .catch(() =>
        setAskAiIdentificationError(
          'Could not generate a prompt. Please try again.',
        ),
      )
      .finally(() => setAskAiIdentificationLoading(false));
  }, [askAiIdentificationPrompt, askAiIdentificationLoading, callAskAiForPrompt, editingSopCategory]);

  // Execution Prompt "Ask AI" — UPDATE in place, mirroring the Routines
  // builder chat bar: the textarea's current text (saved or not) is sent as
  // the base the change is applied to, so asking for a tweak refines what the
  // operator already has instead of regenerating from the category default
  // and wiping it. An empty textarea still means "write a new prompt".
  const editExecutionHasDraft = !!editExecutionPrompt.trim();
  const handleAskAiForExecutionPrompt = useCallback(() => {
    if (!editingSopCategory || askAiExecutionLoading) return;
    const trimmedAsk = askAiExecutionPrompt.trim();
    if (!trimmedAsk) return;
    const categoryName = editingSopCategory.isCustom
      ? 'OTHER'
      : editingSopCategory.title;
    const draftBefore = editExecutionPrompt;
    const isUpdate = !!draftBefore.trim();
    setAskAiExecutionIsUpdate(isUpdate);
    setAskAiExecutionLoading(true);
    setAskAiExecutionError('');
    // The textarea is NOT cleared up front: it keeps showing the draft until
    // the first streamed chunk replaces it.
    callAskAiForPrompt(trimmedAsk, setEditExecutionPrompt, categoryName, {
      existingPrompt: draftBefore.trim(),
      categoryLabel: editingSopCategory.name,
      isCustom: !!editingSopCategory.isCustom,
    })
      .then(() => setAskAiExecutionPrompt(''))
      .catch(() => {
        // A failed or half-streamed response must not cost the operator
        // their draft.
        setEditExecutionPrompt(draftBefore);
        setAskAiExecutionError(
          isUpdate
            ? 'Could not update the prompt. Please try again.'
            : 'Could not generate a prompt. Please try again.',
        );
      })
      .finally(() => setAskAiExecutionLoading(false));
  }, [
    askAiExecutionPrompt,
    askAiExecutionLoading,
    callAskAiForPrompt,
    editingSopCategory,
    editExecutionPrompt,
  ]);

  const newCatExecutionHasDraft = !!newCatExecutionPrompt.trim();
  const handleAskAiForNewExecutionPrompt = useCallback(() => {
    if (askAiNewExecutionLoading) return;
    const trimmedAsk = askAiNewExecutionPrompt.trim();
    if (!trimmedAsk) return;
    const draftBefore = newCatExecutionPrompt;
    const isUpdate = !!draftBefore.trim();
    setAskAiNewExecutionIsUpdate(isUpdate);
    setAskAiNewExecutionLoading(true);
    setAskAiNewExecutionError('');
    // The category doesn't exist server-side yet, so the endpoint gets the
    // generic OTHER type plus the in-progress name as a display label. A new
    // category is always custom: its Execution Prompt is its complete SOP.
    callAskAiForPrompt(trimmedAsk, setNewCatExecutionPrompt, 'OTHER', {
      existingPrompt: draftBefore.trim(),
      categoryLabel: newCatName.trim(),
      isCustom: true,
    })
      .then(() => setAskAiNewExecutionPrompt(''))
      .catch(() => {
        setNewCatExecutionPrompt(draftBefore);
        setAskAiNewExecutionError(
          isUpdate
            ? 'Could not update the prompt. Please try again.'
            : 'Could not generate a prompt. Please try again.',
        );
      })
      .finally(() => setAskAiNewExecutionLoading(false));
  }, [
    askAiNewExecutionPrompt,
    askAiNewExecutionLoading,
    callAskAiForPrompt,
    newCatExecutionPrompt,
    newCatName,
  ]);

  const handleSaveSopCategory = useCallback(() => {
    if (!editingSopCategory || savingSopCategory) return;
    const trimmedTrigger = editTrigger.trim();
    const trimmedCompletion = editCompletion.trim();
    const trimmedIdentification = editIdentificationPrompt.trim();
    const trimmedExecution = editExecutionPrompt.trim();
    const categoryMatchType = editMatchMode === 'label' ? 0 : 1;
    const assigneeType = editAssigneeType;
    const agentName = assigneeType === 2 ? editAssigneeAgentName.trim() : '';
    // Re-resolve by name as a fallback — the ID may never have matched the
    // loaded Digital Workforce list (see editAssigneeSelectValue above), but
    // the name alone is enough to identify the agent to save.
    const agentId = assigneeType === 2
      ? (editAssigneeAgentId.trim() || sopDwAgents.find(a => a.name.trim().toLowerCase() === agentName.toLowerCase())?.id || '')
      : '';
    const authToken = localStorage.getItem('authToken') || '';

    const isCustomCategory = editingSopCategory.isCustom;

    // Same rule as create: a CUSTOM category on Trigger on = All must carry a prompt,
    // since nothing else can select it. Built-in categories keep their existing flow.
    if (editMatchMode === 'label' && !trimmedTrigger) {
      setSaveSopError('Trigger label is required when Trigger on is Label.');
      return;
    }
    if (isCustomCategory && editMatchMode === 'all' && !trimmedIdentification) {
      setSaveSopError(categoryChannel === CHANNEL_EMAIL
        ? 'Identification Prompt is required when Trigger on is Prompt.'
        : 'Identification Prompt is required for Voice and SMS custom categories.');
      return;
    }

    setSavingSopCategory(true);
    setSaveSopError('');

    // Build payload based on category type
    const payload: any = {};

    if (isCustomCategory) {
      // For custom categories, send using custom/upsert endpoint
      payload.id = editingSopCategory.id;
      payload.name = editingSopCategory.name;
      payload.categoryMatchType = categoryMatchType;
      // Trigger label / Identification Prompt swap with the match mode; the
      // inactive one is cleared. Completion label and Execution Prompt are
      // shown in BOTH modes, so they are always sent as typed.
      payload.triggerLabel = editMatchMode === 'label' ? trimmedTrigger : '';
      payload.completionLabel = trimmedCompletion;
      payload.identificationPrompt = editMatchMode === 'all' ? trimmedIdentification : '';
      payload.executionPrompt = trimmedExecution;
    } else {
      // For built-in categories, send using sop-config/upsert endpoint
      payload.kanbanTaskCategoryId = editingSopCategory.id;
      payload.categoryMatchType = categoryMatchType;
      payload.triggerLabel = editMatchMode === 'label' ? trimmedTrigger : '';
      payload.completionLabel = trimmedCompletion;
      payload.identificationPrompt = editMatchMode === 'all' ? trimmedIdentification : '';
      payload.executionPrompt = trimmedExecution;
    }

    payload.channel = categoryChannel;

    // Always include agent fields and assigneeType in payload
    payload.assigneeType = assigneeType;
    payload.agentName = assigneeType === 2 ? agentName : '';
    payload.agentId = assigneeType === 2 ? agentId : '';

    const endpoint = isCustomCategory
      ? `/api/MomentumKanban/task/category/custom/upsert`
      : `/api/MomentumKanban/task/category/sop-config/upsert`;

    if (isCustomCategory) forgetShared('kanbanCustomCategories|');
    fetch(`${(process.env.REACT_APP_Auth_Url || '')}${endpoint}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((r) => (r.ok ? r : Promise.reject()))
      .then(() => {
        if (isCustomCategory) {
          // Update kanbanTaskTypes for custom categories
          setKanbanTaskTypes((prev) =>
            prev.map((cat) =>
              cat.id === editingSopCategory.id
                ? {
                    ...cat,
                    triggerLabel: payload.triggerLabel,
                    completionLabel: payload.completionLabel,
                    identificationPrompt: payload.identificationPrompt,
                    executionPrompt: payload.executionPrompt,
                    matchMode: editMatchMode,
                    assigneeType,
                    assigneeAgentName: agentName,
                    assigneeAgentId: agentId,
                  }
                : cat,
            ),
          );
        } else {
          // Update sopConfigByCategory for built-in categories
          setSopConfigByCategory((prev) => {
            const next = {
              ...prev,
              [editingSopCategory.title]: {
                triggerLabel: payload.triggerLabel,
                completionLabel: payload.completionLabel,
                identificationPrompt: payload.identificationPrompt,
                executionPrompt: payload.executionPrompt,
                matchMode: editMatchMode,
                assigneeType,
                assigneeAgentName: agentName,
                assigneeAgentId: agentId,
              },
            };
            try {
              localStorage.setItem(
                channelStorageKey('kanban_sop_category_config', categoryChannel),
                JSON.stringify(next),
              );
            } catch {
              /* ignore */
            }
            return next;
          });
        }
        setEditingSopCategory(null);
      })
      .catch(() =>
        setSaveSopError('Could not save changes. Please try again.'),
      )
      .finally(() => setSavingSopCategory(false));
  }, [
    editingSopCategory,
    editTrigger,
    editCompletion,
    editIdentificationPrompt,
    editExecutionPrompt,
    editMatchMode,
    editAssigneeType,
    editAssigneeAgentName,
    editAssigneeAgentId,
    savingSopCategory,
    categoryChannel,
  ]);

  // Resolve the edit-SOP assignee select's value by agent NAME rather than
  // ID — the persisted ID may not match the currently loaded Digital
  // Workforce list (still loading, agent renamed/archived), so fall back to
  // a synthetic `dw_name:` option that always shows the saved name.
  const normalizedEditAgentName = editAssigneeAgentName.trim();
  const matchedEditDwAgent = normalizedEditAgentName
    ? sopDwAgents.find(a => a.name.trim().toLowerCase() === normalizedEditAgentName.toLowerCase())
    : undefined;
  const editAssigneeSelectValue = normalizedEditAgentName
    ? (matchedEditDwAgent ? `dw:${matchedEditDwAgent.id}` : `dw_name:${normalizedEditAgentName}`)
    : String(editAssigneeType);

  // ── Command Center dashboard computed values ──
  const ccTotal  = ccStats.meCount + ccStats.aiCount;
  const ccAiPct  = ccTotal > 0 ? Math.round((ccStats.aiCount / ccTotal) * 100) : 0;
  const ccQuickActions = [
    { label: 'New task',icon: <FiPlus size={13} />,      color: '#a200ff', iconBg: 'rgba(162,0,255,0.14)',  route: ROUTES.MOMENTUM_COMMAND + '?new=1' },
    { label: 'Routines',    icon: <FiRefreshCw size={13} />, color: '#00b0d3', iconBg: 'rgba(0,176,211,0.14)',  route: ROUTES.MOMENTUM_ROUTINES  },
    { label: 'Artifacts',   icon: <FiFileText size={13} />,  color: '#6366f1', iconBg: 'rgba(99,102,241,0.14)', route: ROUTES.ARTIFACTS          },
    { label: 'Agents',      icon: <FiUser size={13} />,      color: '#f59e0b', iconBg: 'rgba(245,158,11,0.14)', route: ROUTES.DIGITAL_WORKFORCE  },
  ];

  useEffect(() => {
    if (!configDataRequested || kanbanTypesLoaded) return;
    const channel = categoryChannel;
    const authToken = localStorage.getItem('authToken') || '';
    const headers = { Authorization: `Bearer ${authToken}` };
    Promise.all([
      fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumKanban/task/categories`, {
        headers,
      }).then((r) => (r.ok ? r.json() : Promise.reject())),
      fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumKanban/task/category/configurations?channel=${channel}`, {
        headers,
      }).then((r) => (r.ok ? r.json() : Promise.reject())),
      fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/MomentumKanban/task/category/sop-configs?channel=${channel}`, {
        headers,
      }).then((r) => (r.ok ? r.json() : Promise.reject())),
      // Shared with the Command Center page's own custom/list load on mount.
      fetchKanbanCustomCategoriesRaw(),
      // Custom category Modes (MyAMP). Never rejects: a failure must not blank the panel.
      fetch(`${process.env.REACT_APP_MyAMP_Url}/api/command-center/custom-category-mode`, {
        headers,
      })
        .then((r) => (r.ok ? r.json() : { modes: {} }))
        .catch(() => ({ modes: {} })),
    ])
      .then(([catJson, cfgJson, sopJson, customJson, customModeJson]) => {
        const cats: KanbanTaskCategory[] = catJson?.data ?? [];

        const customCats: KanbanTaskCategory[] = (customJson?.data ?? [])
          .filter((c: any) => (c.channel ?? CHANNEL_EMAIL) === channel)
          .map(
          (c: any): KanbanTaskCategory => ({
            id: c.id,
            name: c.name,
            title: c.title,
            description: c.description ?? '',
            isCustom: true,
            triggerLabel: c.triggerLabel ?? '',
            completionLabel: c.completionLabel ?? '',
            identificationPrompt: c.identificationPrompt ?? '',
            executionPrompt: c.executionPrompt ?? '',
            matchMode: c.categoryMatchType === 1 ? 'all' : 'label',
            // Normalize: if agent name exists, assigneeType should be 2 (DigitalWorkforce)
            assigneeType: readSopAgentName(c) ? 2 : (c.assigneeType ?? 0),
            assigneeAgentName: readSopAgentName(c),
            assigneeAgentId: readSopAgentId(c),
          }),
        );
        const allCats = [...cats, ...customCats];
        setKanbanTaskTypes(allCats);

        const configs: any[] = cfgJson?.data ?? [];
        const MODE_NAMES: KanbanTaskMode[] = ['auto', 'manual', 'disabled'];
        const modeMap: Record<string, KanbanTaskMode> = {};
        configs.forEach((cfg: any) => {
          const cat = allCats.find((c) => c.id === cfg.kanbanTaskCategoryId);
          if (cat) modeMap[cat.title] = MODE_NAMES[cfg.mode] ?? 'disabled';
        });
        // Custom category Modes, applied last so a saved one wins for its own title.
        const customModes: Record<string, number> = customModeJson?.modes ?? {};
        customCats.forEach((c) => {
          const raw = customModes[String(c.id)];
          if (raw !== undefined && raw !== null) {
            modeMap[c.title] = MODE_NAMES[raw] ?? 'disabled';
          }
        });
        const nextModes = { ...readChannelCache<KanbanTaskMode>('kanban_type_config', channel), ...modeMap };
        localStorage.setItem(channelStorageKey('kanban_type_config', channel), JSON.stringify(nextModes));
        setKanbanTypeConfig(nextModes);

        const sopConfigs: any[] = sopJson?.data ?? [];
        const sopMap: Record<string, SopCategoryConfig> = {};
        sopConfigs.forEach((cfg: any) => {
          const cat = cats.find((c) => c.id === cfg.kanbanTaskCategoryId);
          if (cat) {
            const agentName = readSopAgentName(cfg);
            // Normalize: if agent name exists, assigneeType should be 2 (DigitalWorkforce)
            const assigneeType = agentName ? 2 : (cfg.assigneeType ?? 0);
            sopMap[cat.title] = {
              triggerLabel: cfg.triggerLabel ?? '',
              completionLabel: cfg.completionLabel ?? '',
              identificationPrompt: cfg.identificationPrompt ?? '',
              executionPrompt: cfg.executionPrompt ?? '',
              matchMode: cfg.categoryMatchType === 1 ? 'all' : 'label',
              assigneeType,
              assigneeAgentName: agentName,
              assigneeAgentId: readSopAgentId(cfg),
            };
          }
        });
        const nextSop = { ...readChannelCache<SopCategoryConfig>('kanban_sop_category_config', channel), ...sopMap };
        try {
          localStorage.setItem(channelStorageKey('kanban_sop_category_config', channel), JSON.stringify(nextSop));
        } catch {
          /* ignore */
        }
        setSopConfigByCategory(nextSop);

        setLoadedChannel(channel);
      })
      .catch(() => setLoadedChannel(channel));
  }, [configDataRequested, kanbanTypesLoaded, categoryChannel]);

  // Each channel loads from the server once per page load; switching back restores it from here.
  const channelCacheRef = useRef<Partial<Record<CategoryChannel, {
    types: KanbanTaskCategory[];
    modes: Record<string, KanbanTaskMode>;
    sop: Record<string, SopCategoryConfig>;
  }>>>({});
  useEffect(() => {
    if (!kanbanTypesLoaded) return;
    channelCacheRef.current[categoryChannel] = {
      types: kanbanTaskTypes,
      modes: kanbanTypeConfig,
      sop: sopConfigByCategory,
    };
  }, [kanbanTypesLoaded, categoryChannel, kanbanTaskTypes, kanbanTypeConfig, sopConfigByCategory]);

  // Built-in identification prompts, shown for a Voice/SMS built-in category whose own prompt
  // is empty — the AI matcher uses them in that case.
  useEffect(() => {
    if (!configDataRequested) return;
    const authToken = localStorage.getItem('authToken') || '';
    fetch(`${process.env.REACT_APP_MyAMP_Url}/api/command-center/default-category-prompts`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => (r.ok ? r.json() : { prompts: {} }))
      .then((json) => setDefaultCategoryPrompts(json?.prompts ?? {}))
      .catch(() => {});
  }, [configDataRequested]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* ── Tab bar ── */}
        <div className="cc-sb-tabs">
          <div className="cc-sb-tab-track">
            <button
              className={`cc-sb-tab${kanbanSidebarTab === 'dashboard' ? ' cc-sb-tab--active cc-sb-tab--active-dashboard' : ''}`}
              type='button'
              aria-pressed={kanbanSidebarTab === 'dashboard'}
              onClick={() => setKanbanSidebarTab('dashboard')}
            >
              <FiBarChart2 size={11} aria-hidden="true" />
              Analytics
            </button>
            <button
              className={`cc-sb-tab${kanbanSidebarTab === 'config' ? ' cc-sb-tab--active cc-sb-tab--active-config' : ''}`}
              type='button'
              aria-pressed={kanbanSidebarTab === 'config'}
              onClick={() => setKanbanSidebarTab('config')}
            >
              <FiSettings size={11} aria-hidden="true" />
              Configuration
            </button>
          </div>
        </div>

        {/* ── Dashboard tab content ── */}
        {kanbanSidebarTab === 'dashboard' && (
            <div className="cc-sb-dashboard">
              {/* Stats — 1 per row, horizontal layout */}
              <div className="cc-sb-stats">
                {/* OVERDUE is an honest placeholder: due dates are not tracked yet
                    (MomentumCommandPage feeds overdueCount: 0), so it renders neutral
                    with "—" instead of a red fake zero. */}
                {([
                  { cls: 'cc-sb-stat--amber',   lbl: 'ASSIGNED TO ME', ctx: 'Active tasks',              val: ccStats.meCount,   numCls: 'cc-sb-stat__num--amber'   },
                  { cls: 'cc-sb-stat--purple',  lbl: 'ASSIGNED TO AI', ctx: 'AI-handled tasks',          val: ccStats.aiCount,   numCls: 'cc-sb-stat__num--purple'  },
                  { cls: 'cc-sb-stat--green',   lbl: 'COMPLETED',      ctx: 'All time',                  val: ccStats.doneCount, numCls: 'cc-sb-stat__num--green'   },
                  { cls: 'cc-sb-stat--neutral', lbl: 'OVERDUE',        ctx: 'Due dates not tracked yet', val: '—',               numCls: 'cc-sb-stat__num--neutral', srVal: 'Not available' },
                ] as { cls: string; lbl: string; ctx: string; val: number | string; numCls: string; srVal?: string }[]).map((s, i) => (
                  <div key={i} className={`cc-sb-stat ${s.cls}`}>
                    <div className="cc-sb-stat__body">
                      <span className="cc-sb-stat__label">{s.lbl}</span>
                      <span className="cc-sb-stat__context">{s.ctx}</span>
                    </div>
                    {s.srVal ? (
                      <span className={`cc-sb-stat__num ${s.numCls}`}>
                        <span aria-hidden="true">{s.val}</span>
                        <span style={SR_ONLY}>{s.srVal}</span>
                      </span>
                    ) : (
                      <span className={`cc-sb-stat__num ${s.numCls}`}>{s.val}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="cc-sb-quick-actions">
                <div className="cc-sb-section-lbl">Quick Actions</div>
                <div className="cc-sb-qa-grid">
                  {ccQuickActions.map((a, i) => (
                    <button key={i} type="button" className="cc-sb-qa-btn" onClick={() => navigate(a.route)}>
                      <div className="cc-sb-qa-icon" style={{ background: a.iconBg, color: a.color }}>{a.icon}</div>
                      <span className="cc-sb-qa-label">{a.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Workload Split */}
              <div className="cc-sb-workload">
                <div className="cc-sb-section-lbl">Workload Split</div>
                <div className="cc-sb-split-card">
                  {/* Segmented bar */}
                  <div className="cc-sb-split-bar">
                    {ccTotal > 0 ? (
                      <>
                        <div className="cc-sb-split-bar__me" style={{ width: `${Math.round((ccStats.meCount / ccTotal) * 100)}%` }} />
                        <div className="cc-sb-split-bar__ai" style={{ width: `${ccAiPct}%` }} />
                      </>
                    ) : (
                      <div style={{ flex: 1, background: 'rgba(255,255,255,0.06)', borderRadius: 99 }} />
                    )}
                  </div>
                  {/* Legend */}
                  <div className="cc-sb-split-legend">
                    <div className="cc-sb-split-legend__item">
                      <span className="cc-sb-split-legend__dot cc-sb-split-legend__dot--me" />
                      <div className="cc-sb-split-legend__info">
                        <span className="cc-sb-split-legend__count">{ccStats.meCount}</span>
                        <span className="cc-sb-split-legend__label">Assigned to Me</span>
                      </div>
                      <span className="cc-sb-split-legend__pct">{ccTotal > 0 ? Math.round((ccStats.meCount / ccTotal) * 100) : 0}%</span>
                    </div>
                    <div className="cc-sb-split-legend__item">
                      <span className="cc-sb-split-legend__dot cc-sb-split-legend__dot--ai" />
                      <div className="cc-sb-split-legend__info">
                        <span className="cc-sb-split-legend__count">{ccStats.aiCount}</span>
                        <span className="cc-sb-split-legend__label">Handled by AI</span>
                      </div>
                      <span className="cc-sb-split-legend__pct">{ccAiPct}%</span>
                    </div>
                  </div>
                  {/* All-time health (doneCount is all-time — see COMPLETED stat above) */}
                  <div className="cc-sb-week-row">
                    <div className={`cc-sb-week-chip cc-sb-week-chip--done${ccStats.doneCount > 0 ? ' cc-sb-week-chip--active' : ''}`}>
                      <FiCheck size={10} />
                      <span>{ccStats.doneCount} Done</span>
                    </div>
                    {/* Neutral placeholder until due dates are tracked (see OVERDUE stat). */}
                    <div className="cc-sb-week-chip cc-sb-week-chip--overdue" title="Due dates not tracked yet">
                      <FiAlertTriangle size={10} aria-hidden="true" />
                      <span>Overdue <span aria-hidden="true">—</span><span style={SR_ONLY}>not tracked yet</span></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        )}

        {/* ── Configuration tab content ── */}
        {kanbanSidebarTab === 'config' && (
        <div style={{ flex: 1, overflowY: 'auto' }}>
        <div
          style={{
            padding: '14px 16px 20px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* AMS email filter — SEPARATE from Command Center. This Gmail label decides
              which emails are logged to the AMS activity feed, not task routing. */}
          <div
            style={{
              marginBottom: 16,
              padding: '12px 12px 14px',
              background:
                'linear-gradient(180deg, rgba(0,176,211,0.06), rgba(0,176,211,0.02))',
              borderRadius: 8,
              border: '1px solid rgba(0,176,211,0.28)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginBottom: 4,
              }}
            >
              <span aria-hidden style={{ fontSize: 13 }}>🏷️</span>
              Email → AMS Activity Logs
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
                lineHeight: 1.5,
                marginBottom: 8,
              }}
            >
              Only emails with one of these Gmail labels go to the AMS
              (comma-separated).
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                className='mc-a11y-field'
                type='text'
                value={processLabelInput}
                onChange={(e) => setProcessLabelInput(e.target.value)}
                placeholder='Leave empty to send all emails'
                disabled={!processLabelLoaded || processLabelSaving}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '7px 10px',
                  fontSize: 12,
                  borderRadius: 6,
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                }}
              />
              <button
                type='button'
                onClick={saveProcessLabel}
                disabled={
                  !processLabelLoaded ||
                  processLabelSaving ||
                  processLabelInput.trim() === processLabel.trim()
                }
                style={{
                  padding: '7px 12px',
                  fontSize: 12,
                  fontWeight: 700,
                  borderRadius: 6,
                  border: '1px solid rgba(139,92,246,0.35)',
                  background:
                    !processLabelLoaded ||
                    processLabelSaving ||
                    processLabelInput.trim() === processLabel.trim()
                      ? 'var(--bg-secondary)'
                      : 'rgba(139,92,246,0.12)',
                  color: 'var(--accent-purple)',
                  cursor:
                    !processLabelLoaded ||
                    processLabelSaving ||
                    processLabelInput.trim() === processLabel.trim()
                      ? 'default'
                      : 'pointer',
                  flexShrink: 0,
                }}
              >
                {processLabelSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          {/* ── Divider: everything below is Command Center task routing ── */}
          <div
            style={{
              borderTop: '1px solid var(--border-subtle)',
              margin: '2px 0 14px',
            }}
          />
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.3,
              textTransform: 'uppercase',
              color: 'var(--text-tertiary)',
              marginBottom: 4,
            }}
          >
            Command Center task routing
          </div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--text-tertiary)',
              lineHeight: 1.5,
              marginBottom: 12,
            }}
          >
            Configure how each incoming task type is routed to the Command
            Center.
          </div>

          <div
            role='tablist'
            aria-label='Category channel'
            style={{ display: 'inline-flex', alignSelf: 'flex-start', border: '1px solid var(--border-glass)', borderRadius: 8, padding: 3, gap: 3, background: 'var(--bg-primary)', marginBottom: 6 }}
          >
            {CATEGORY_CHANNELS.map((ch) => (
              <button
                key={ch.id}
                type='button'
                role='tab'
                aria-selected={categoryChannel === ch.id}
                className='mc-a11y-btn'
                onClick={() => {
                  if (ch.id === categoryChannel) return;
                  const cached = channelCacheRef.current[ch.id];
                  if (cached) {
                    setKanbanTaskTypes(cached.types);
                    setKanbanTypeConfig(cached.modes);
                    setSopConfigByCategory(cached.sop);
                    setLoadedChannel(ch.id);
                  } else {
                    setKanbanTaskTypes([]);
                  }
                  setCategoryChannel(ch.id);
                }}
                style={{
                  padding: '6px 16px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  background: categoryChannel === ch.id ? '#7c3aed' : 'transparent',
                  color: categoryChannel === ch.id ? '#fff' : 'var(--text-secondary)',
                }}
              >
                {ch.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5, marginBottom: 12 }}>
            {CATEGORY_CHANNELS.find((ch) => ch.id === categoryChannel)?.hint}
          </div>

          {/* Legend */}
          <div
            style={{
              display: 'flex',
              gap: 12,
              marginBottom: 12,
              padding: '7px 10px',
              background: 'var(--bg-secondary)',
              borderRadius: 7,
              border: '1px solid var(--border-subtle)',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                color: 'var(--text-tertiary)',
              }}
            >
              <FiZap size={10} style={{ color: '#22c55e' }} /> Auto
            </span>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                color: 'var(--text-tertiary)',
              }}
            >
              <FiUser size={10} style={{ color: '#f59e0b' }} /> Manual
            </span>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                color: 'var(--text-tertiary)',
              }}
            >
              <FiSlash size={10} style={{ color: '#ef4444' }} /> Disabled
            </span>
          </div>

          {/* New category trigger */}
          <button
            type='button'
            onClick={() => setShowNewSopCategoryModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              width: '100%',
              padding: '9px 0',
              marginBottom: 12,
              borderRadius: 7,
              border: '1px solid rgba(139,92,246,0.35)',
              background: 'rgba(139,92,246,0.12)',
              color: 'var(--accent-purple)',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <FiPlus size={12} /> New category
          </button>

          {/* Task type rows */}
          {!kanbanTypesLoaded && kanbanTaskTypes.length === 0 && (
            <div
              style={{
                padding: '20px 0',
                textAlign: 'center',
                fontSize: 12,
                color: 'var(--text-tertiary)',
              }}
            >
              <FiRefreshCw
                size={13}
                style={{
                  animation: 'spin 1s linear infinite',
                  marginBottom: 6,
                }}
              />
              <div>Loading task types…</div>
            </div>
          )}
          {kanbanTaskTypes.map((cat, i) => {
            const meta = KANBAN_TYPE_META[cat.title] ?? {
              color: '#6b7280',
              icon: FiZap,
            };
            const Icon = meta.icon;
            const mode = kanbanTypeConfig[cat.title] ?? 'disabled';
            return (
              <div
                key={cat.title}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 0',
                  borderBottom:
                    i < kanbanTaskTypes.length - 1
                      ? '1px solid var(--border-subtle)'
                      : 'none',
                }}
              >
                {/* Type icon */}
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: `${meta.color}18`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon size={13} style={{ color: meta.color }} />
                </div>
                {/* Label + description. The Custom tag sits under the name so the
                    name gets the full row width instead of being cut short. */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 3,
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      lineHeight: 1.3,
                    }}
                  >
                    <span
                      title={cat.name}
                      style={{
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {cat.name}
                    </span>
                    {cat.isCustom && (
                      <span
                        style={{
                          fontSize: 9.5,
                          fontWeight: 700,
                          color: '#a78bfa',
                          background: 'rgba(139,92,246,0.14)',
                          border: '1px solid rgba(139,92,246,0.3)',
                          borderRadius: 'var(--radius-xs)',
                          padding: '1px 7px',
                          lineHeight: 1.4,
                        }}
                      >
                        Custom
                      </span>
                    )}
                  </div>
                  <div
                    title={cat.description || undefined}
                    style={{
                      fontSize: 11,
                      color: 'var(--text-tertiary)',
                      lineHeight: 1.4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {cat.description}
                  </div>
                </div>
                {/* Mode toggle buttons */}
                <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                  <button
                    type='button'
                    className='mc-a11y-btn'
                    title='Auto Run'
                    aria-label={`Auto run ${cat.name}`}
                    aria-pressed={mode === 'auto'}
                    onClick={() =>
                      setKanbanTypeMode(cat.title, cat.id, 'auto', !!cat.isCustom)
                    }
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 5,
                      border: '1px solid',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
                      background:
                        mode === 'auto'
                          ? 'rgba(34,197,94,0.12)'
                          : 'transparent',
                      borderColor:
                        mode === 'auto'
                          ? '#22c55e'
                          : 'var(--border-subtle)',
                      color:
                        mode === 'auto'
                          ? '#22c55e'
                          : 'var(--text-tertiary)',
                    }}
                  >
                    <FiZap size={11} />
                  </button>
                  <button
                    type='button'
                    className='mc-a11y-btn'
                    title='Wait for Human Approval'
                    aria-label={`Wait for human approval on ${cat.name}`}
                    aria-pressed={mode === 'manual'}
                    onClick={() =>
                      setKanbanTypeMode(cat.title, cat.id, 'manual', !!cat.isCustom)
                    }
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 5,
                      border: '1px solid',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
                      background:
                        mode === 'manual'
                          ? 'rgba(245,158,11,0.15)'
                          : 'transparent',
                      borderColor:
                        mode === 'manual'
                          ? '#f59e0b'
                          : 'var(--border-subtle)',
                      color:
                        mode === 'manual'
                          ? '#f59e0b'
                          : 'var(--text-tertiary)',
                    }}
                  >
                    <FiUser size={11} />
                  </button>
                  <button
                    type='button'
                    className='mc-a11y-btn'
                    title='Disable'
                    aria-label={`Disable ${cat.name}`}
                    aria-pressed={mode === 'disabled'}
                    onClick={() =>
                      setKanbanTypeMode(cat.title, cat.id, 'disabled', !!cat.isCustom)
                    }
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 5,
                      border: '1px solid',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
                      background:
                        mode === 'disabled'
                          ? 'rgba(239,68,68,0.12)'
                          : 'transparent',
                      borderColor:
                        mode === 'disabled'
                          ? '#ef4444'
                          : 'var(--border-subtle)',
                      color:
                        mode === 'disabled'
                          ? '#ef4444'
                          : 'var(--text-tertiary)',
                    }}
                  >
                    <FiSlash size={11} />
                  </button>
                  <button
                    type='button'
                    className='mc-a11y-btn'
                    title='Configure SOP'
                    aria-label={`Configure SOP for ${cat.name}`}
                    onClick={() => openEditSopModal(cat)}
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 5,
                      border: '1px solid var(--border-subtle)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
                      background: 'transparent',
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    <FiEdit3 size={11} />
                  </button>
                  {cat.isCustom && (
                    <button
                      type='button'
                      className='mc-a11y-btn'
                      title='Share category'
                      aria-label={`Share ${cat.name}`}
                      onClick={() => setSharingSopCategory(cat)}
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 5,
                        border: '1px solid var(--border-subtle)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
                        background: 'transparent',
                        color: '#60a5fa',
                      }}
                    >
                      <FiShare2 size={11} />
                    </button>
                  )}
                  {cat.isCustom && (
                    <button
                      title='Delete category'
                      aria-label={`Delete ${cat.name}`}
                      onClick={() => {
                        setDeleteSopError('');
                        setDeletingSopCategory(cat);
                      }}
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 5,
                        border: '1px solid var(--border-subtle)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        background: 'transparent',
                        color: '#ef4444',
                      }}
                    >
                      <FiTrash2 size={11} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

        </div>
        </div>
        )}
      </div>

      {/* ── New SOP category modal (portalled to body) ── */}
      {showNewSopCategoryModal &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10000,
              background: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 620,
                background: 'var(--bg-secondary)',
                borderRadius: 12,
                border: '1px solid var(--border-glass)',
                boxShadow: '0 32px 80px rgba(0,0,0,0.35)',
                padding: 20,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  marginBottom: 6,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiPlus size={16} style={{ color: 'var(--text-primary)' }} />
                  <span
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: 'var(--text-primary)',
                    }}
                  >
                    New SOP category
                  </span>
                </div>
                <button
                  onClick={closeNewSopCategoryModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-tertiary)',
                    cursor: 'pointer',
                    padding: 2,
                    lineHeight: 1,
                  }}
                  type='button'
                  className='mc-a11y-btn'
                  title='Close'
                  aria-label='Close'
                >
                  <FiX size={16} />
                </button>
              </div>
              <p
                style={{
                  margin: '0 0 18px',
                  fontSize: 12.5,
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5,
                }}
              >
                Define a custom label the AI listens for and the SOP it should
                follow.
              </p>

              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    marginBottom: 6,
                  }}
                >
                  Category name
                </label>
                <input
                  className='mc-a11y-field'
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '9px 12px',
                    borderRadius: 8,
                    border: '1px solid var(--border-glass)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: 13,
                  }}
                />
              </div>

              {categoryChannel === CHANNEL_EMAIL && (
                <div style={{ marginBottom: 14 }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      marginBottom: 6,
                    }}
                  >
                    Trigger on
                  </label>
                  <div style={{ display: 'inline-flex', border: '1px solid var(--border-glass)', borderRadius: 8, padding: 3, gap: 3, background: 'var(--bg-primary)' }}>
                    {(['label', 'all'] as const).map((m) => (
                      <button
                        key={m}
                        type='button'
                        onClick={() => {
                          setNewCatMatchMode(m);
                          if (createSopError) setCreateSopError('');
                        }}
                        aria-pressed={newCatMatchMode === m}
                        className='mc-a11y-btn'
                        style={{
                          padding: '8px 18px',
                          borderRadius: 6,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: 'inherit',
                          transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
                          background: newCatMatchMode === m ? '#7c3aed' : 'transparent',
                          color: newCatMatchMode === m ? '#fff' : 'var(--text-secondary)',
                          boxShadow: newCatMatchMode === m ? 'inset 0 2px 6px rgba(124, 58, 237, 0.3)' : 'none',
                        }}
                      >
                        {m === 'label' ? 'Label' : 'Prompt'}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {newCatMatchMode === 'label' ? (
                <div style={{ marginBottom: 14 }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      marginBottom: 6,
                    }}
                  >
                    Trigger label <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    className='mc-a11y-field'
                    value={newCatTrigger}
                    onChange={(e) => setNewCatTrigger(e.target.value)}
                    placeholder='Label that triggers this category'
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '9px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border-glass)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: 13,
                    }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5, marginTop: 6 }}>
                    Separate several labels with commas.
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: 14 }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      marginBottom: 6,
                    }}
                  >
                    Identification Prompt <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <textarea
                    className='mc-a11y-field'
                    value={newCatIdentificationPrompt}
                    onChange={(e) => {
                      setNewCatIdentificationPrompt(e.target.value);
                      if (createSopError) setCreateSopError('');
                    }}
                    placeholder='Describe how the AI should identify that this category applies to a task.'
                    rows={7}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '9px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border-glass)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: 13,
                      resize: 'vertical',
                      lineHeight: 1.5,
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div style={{ display: categoryChannel === CHANNEL_EMAIL ? undefined : 'none' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      marginBottom: 6,
                    }}
                  >
                    Completion label
                  </label>
                  <input
                    className='mc-a11y-field'
                    value={newCatCompletion}
                    onChange={(e) => setNewCatCompletion(e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '9px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border-glass)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: 13,
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      marginBottom: 6,
                    }}
                  >
                    Assignee
                  </label>
                  <select
                    className='mc-a11y-field'
                    value={newCatAssigneeAgentId ? `dw:${newCatAssigneeAgentId}` : String(newCatAssigneeType)}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v.startsWith('dw:')) {
                        const selectedAgentId = v.slice(3);
                        const selectedAgent = sopDwAgents.find(a => a.id === selectedAgentId);
                        setNewCatAssigneeType(2);
                        setNewCatAssigneeAgentId(selectedAgentId);
                        setNewCatAssigneeAgentName(selectedAgent?.name ?? '');
                      } else {
                        setNewCatAssigneeType(Number(v));
                        setNewCatAssigneeAgentId('');
                        setNewCatAssigneeAgentName('');
                      }
                    }}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '9px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border-glass)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    {SOP_ASSIGNEE_TYPES.map((a) => (
                      <option key={a.value} value={String(a.value)}>{a.label}</option>
                    ))}
                    {sopDwAgents.length > 0 && (
                      <>
                        <option disabled>── Digital Workforce ──</option>
                        {sopDwAgents.map((a) => (
                          <option key={`dw:${a.id}`} value={`dw:${a.id}`}>{a.isPrimary ? `★ ${a.name} (Primary)` : a.name}</option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    marginBottom: 6,
                  }}
                >
                  Execution Prompt
                </label>
                <textarea
                  className='mc-a11y-field'
                  value={newCatExecutionPrompt}
                  onChange={(e) => setNewCatExecutionPrompt(e.target.value)}
                  placeholder='Describe the steps the AI should follow once this category is applied.'
                  rows={4}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '9px 12px',
                    borderRadius: 8,
                    border: '1px solid var(--border-glass)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: 13,
                    resize: 'vertical',
                    lineHeight: 1.5,
                    fontFamily: 'inherit',
                  }}
                />
                <div
                  style={{
                    marginTop: 8,
                    padding: 10,
                    borderRadius: 8,
                    border: '1px dashed var(--border-glass)',
                    background: 'rgba(162,0,255,0.05)',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      marginBottom: 6,
                    }}
                  >
                    <FiZap size={11} style={{ color: '#a200ff' }} />
                    {newCatExecutionHasDraft
                      ? 'Ask AI to update this prompt'
                      : 'Ask AI for prompt'}
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className='mc-a11y-field'
                      value={askAiNewExecutionPrompt}
                      onChange={(e) =>
                        setAskAiNewExecutionPrompt(e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !askAiNewExecutionLoading) {
                          e.preventDefault();
                          handleAskAiForNewExecutionPrompt();
                        }
                      }}
                      placeholder={
                        newCatExecutionHasDraft
                          ? 'Tell the AI what to change in the prompt above, e.g. "make it shorter" or "always CC the producer"'
                          : 'Tell the AI what to add or change, e.g. "Always CC the producer email"'
                      }
                      disabled={askAiNewExecutionLoading}
                      style={{
                        flex: 1,
                        boxSizing: 'border-box',
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: '1px solid var(--border-glass)',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        fontSize: 12.5,
                      }}
                    />
                    <button
                      onClick={handleAskAiForNewExecutionPrompt}
                      disabled={
                        askAiNewExecutionLoading || !askAiNewExecutionPrompt.trim()
                      }
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: 'none',
                        fontSize: 12.5,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        cursor:
                          askAiNewExecutionLoading ||
                          !askAiNewExecutionPrompt.trim()
                            ? 'not-allowed'
                            : 'pointer',
                        opacity:
                          askAiNewExecutionLoading || !askAiNewExecutionPrompt.trim()
                            ? 0.6
                            : 1,
                        background:
                          'linear-gradient(135deg, #6d28d9, #a200ff)',
                        color: 'white',
                      }}
                    >
                      {askAiNewExecutionLoading
                        ? askAiNewExecutionIsUpdate
                          ? 'Updating…'
                          : 'Generating…'
                        : newCatExecutionHasDraft
                          ? 'Update'
                          : 'Generate'}
                    </button>
                  </div>
                  {askAiNewExecutionError && (
                    <p
                      style={{
                        margin: '8px 0 0',
                        fontSize: 11.5,
                        color: '#ef4444',
                      }}
                    >
                      {askAiNewExecutionError}
                    </p>
                  )}
                </div>
              </div>

              {createSopError && (
                <p
                  style={{
                    margin: '0 0 10px',
                    fontSize: 11.5,
                    color: '#ef4444',
                    textAlign: 'right',
                  }}
                >
                  {createSopError}
                </p>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleCreateSopCategory}
                  disabled={!newCatCanSave || creatingSopCategory}
                  style={{
                    padding: '9px 18px',
                    borderRadius: 8,
                    border: 'none',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor:
                      newCatCanSave && !creatingSopCategory ? 'pointer' : 'not-allowed',
                    background:
                      newCatCanSave && !creatingSopCategory
                        ? 'linear-gradient(135deg, #6d28d9, #a200ff)'
                        : 'var(--border-glass)',
                    color:
                      newCatCanSave && !creatingSopCategory
                        ? '#fff'
                        : 'var(--text-tertiary)',
                  }}
                >
                  {creatingSopCategory ? 'Creating…' : 'Create category'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* ── Edit SOP category modal (portalled to body) ── */}
      {editingSopCategory &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10000,
              background: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
            }}
          >
            <div
              style={{
                width: '100%',
                maxWidth: 620,
                maxHeight: '88vh',
                overflowY: 'auto',
                background: 'var(--bg-secondary)',
                borderRadius: 12,
                border: '1px solid var(--border-glass)',
                boxShadow: '0 32px 80px rgba(0,0,0,0.35)',
                padding: 24,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  marginBottom: 6,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiFileText
                    size={16}
                    style={{ color: 'var(--text-primary)' }}
                  />
                  <span
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: 'var(--text-primary)',
                    }}
                  >
                    {editingSopCategory.name}
                  </span>
                </div>
                <button
                  onClick={closeEditSopModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-tertiary)',
                    cursor: 'pointer',
                    padding: 2,
                    lineHeight: 1,
                  }}
                  type='button'
                  className='mc-a11y-btn'
                  title='Close'
                  aria-label='Close'
                >
                  <FiX size={16} />
                </button>
              </div>
              <p
                style={{
                  margin: '0 0 18px',
                  fontSize: 12.5,
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5,
                }}
              >
                Configure how the AI handles emails with this label.
              </p>

              {categoryChannel === CHANNEL_EMAIL && (
                <div style={{ marginBottom: 14 }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      marginBottom: 6,
                    }}
                  >
                    Trigger on
                  </label>
                  <div style={{ display: 'inline-flex', border: '1px solid var(--border-glass)', borderRadius: 8, padding: 3, gap: 3, background: 'var(--bg-primary)' }}>
                    {(['label', 'all'] as const).map((m) => (
                      <button
                        key={m}
                        type='button'
                        onClick={() => {
                          setEditMatchMode(m);
                          if (saveSopError) setSaveSopError('');
                        }}
                        aria-pressed={editMatchMode === m}
                        className='mc-a11y-btn'
                        style={{
                          padding: '8px 18px',
                          borderRadius: 6,
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: 'inherit',
                          transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
                          background: editMatchMode === m ? '#7c3aed' : 'transparent',
                          color: editMatchMode === m ? '#fff' : 'var(--text-secondary)',
                          boxShadow: editMatchMode === m ? 'inset 0 2px 6px rgba(124, 58, 237, 0.3)' : 'none',
                        }}
                      >
                        {m === 'label' ? 'Label' : 'Prompt'}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {editMatchMode === 'label' ? (
                <div style={{ marginBottom: 14 }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      marginBottom: 6,
                    }}
                  >
                    Trigger label <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    className='mc-a11y-field'
                    value={editTrigger}
                    onChange={(e) => setEditTrigger(e.target.value)}
                    placeholder='Label that triggers this category'
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '9px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border-glass)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: 13,
                    }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5, marginTop: 6 }}>
                    Separate several labels with commas.
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: 16 }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      marginBottom: 6,
                    }}
                  >
                    Identification Prompt{' '}
                    {editingSopCategory?.isCustom && (
                      <span style={{ color: '#ef4444' }}>*</span>
                    )}
                  </label>
                  <textarea
                    className='mc-a11y-field'
                    value={editIdentificationPrompt}
                    onChange={(e) => {
                      setEditIdentificationPrompt(e.target.value);
                      if (saveSopError) setSaveSopError('');
                    }}
                    placeholder={
                      (categoryChannel !== CHANNEL_EMAIL && !editingSopCategory?.isCustom && defaultCategoryPrompts[editingSopCategory?.title ?? '']) ||
                      'Describe how the AI should identify that this category applies to a task.'
                    }
                    rows={7}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '9px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border-glass)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: 13,
                      resize: 'vertical',
                      lineHeight: 1.5,
                      fontFamily: 'inherit',
                    }}
                  />
                  {categoryChannel !== CHANNEL_EMAIL && !editingSopCategory?.isCustom && defaultCategoryPrompts[editingSopCategory?.title ?? ''] && (
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5, marginTop: 6 }}>
                      Left empty, the default prompt shown above is used.
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div style={{ display: categoryChannel === CHANNEL_EMAIL ? undefined : 'none' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      marginBottom: 6,
                    }}
                  >
                    Completion label
                  </label>
                  <input
                    className='mc-a11y-field'
                    value={editCompletion}
                    onChange={(e) => setEditCompletion(e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '9px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border-glass)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: 13,
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      marginBottom: 6,
                    }}
                  >
                    Assignee
                  </label>
                  <select
                    className='mc-a11y-field'
                    value={editAssigneeSelectValue}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v.startsWith('dw:')) {
                        const selectedAgentId = v.slice(3);
                        const selectedAgent = sopDwAgents.find(a => a.id === selectedAgentId);
                        setEditAssigneeType(2);
                        setEditAssigneeAgentId(selectedAgentId);
                        setEditAssigneeAgentName(selectedAgent?.name ?? '');
                      } else if (v.startsWith('dw_name:')) {
                        setEditAssigneeType(2);
                        setEditAssigneeAgentId('');
                        setEditAssigneeAgentName(v.slice('dw_name:'.length));
                      } else {
                        setEditAssigneeType(Number(v));
                        setEditAssigneeAgentId('');
                        setEditAssigneeAgentName('');
                      }
                    }}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '9px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border-glass)',
                      background: 'var(--bg-primary)',
                      color: 'var(--text-primary)',
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    {SOP_ASSIGNEE_TYPES.map((a) => (
                      <option key={a.value} value={String(a.value)}>{a.label}</option>
                    ))}
                    {sopDwAgents.length > 0 && (
                      <>
                        <option disabled>── Digital Workforce ──</option>
                        {sopDwAgents.map((a) => (
                          <option key={`dw:${a.id}`} value={`dw:${a.id}`}>{a.isPrimary ? `★ ${a.name} (Primary)` : a.name}</option>
                        ))}
                      </>
                    )}
                    {normalizedEditAgentName && !matchedEditDwAgent && (
                      <option value={`dw_name:${normalizedEditAgentName}`}>{normalizedEditAgentName}</option>
                    )}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    marginBottom: 6,
                  }}
                >
                  Execution Prompt
                </label>
                {editingSopCategory && !editingSopCategory.isCustom && (
                  <p
                    style={{
                      margin: '-2px 0 6px',
                      fontSize: 11.5,
                      lineHeight: 1.4,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Added on top of the built-in steps for this category. The
                    AI follows both, and these instructions win where they
                    conflict.
                  </p>
                )}
                <textarea
                  className='mc-a11y-field'
                  value={editExecutionPrompt}
                  onChange={(e) => setEditExecutionPrompt(e.target.value)}
                  placeholder='Describe the steps the AI should follow once this category is applied.'
                  rows={4}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '9px 12px',
                    borderRadius: 8,
                    border: '1px solid var(--border-glass)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: 13,
                    resize: 'vertical',
                    lineHeight: 1.5,
                    fontFamily: 'inherit',
                  }}
                />
                <div
                  style={{
                    marginTop: 8,
                    padding: 10,
                    borderRadius: 8,
                    border: '1px dashed var(--border-glass)',
                    background: 'rgba(162,0,255,0.05)',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      marginBottom: 6,
                    }}
                  >
                    <FiZap size={11} style={{ color: '#a200ff' }} />
                    {editExecutionHasDraft
                      ? 'Ask AI to update this prompt'
                      : 'Ask AI for prompt'}
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className='mc-a11y-field'
                      value={askAiExecutionPrompt}
                      onChange={(e) =>
                        setAskAiExecutionPrompt(e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !askAiExecutionLoading) {
                          e.preventDefault();
                          handleAskAiForExecutionPrompt();
                        }
                      }}
                      placeholder={
                        editExecutionHasDraft
                          ? 'Tell the AI what to change in the prompt above, e.g. "make it shorter" or "always CC the producer"'
                          : 'Tell the AI what to add or change, e.g. "Always CC the producer email"'
                      }
                      disabled={askAiExecutionLoading}
                      style={{
                        flex: 1,
                        boxSizing: 'border-box',
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: '1px solid var(--border-glass)',
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        fontSize: 12.5,
                      }}
                    />
                    <button
                      onClick={handleAskAiForExecutionPrompt}
                      disabled={
                        askAiExecutionLoading || !askAiExecutionPrompt.trim()
                      }
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: 'none',
                        fontSize: 12.5,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        cursor:
                          askAiExecutionLoading ||
                          !askAiExecutionPrompt.trim()
                            ? 'not-allowed'
                            : 'pointer',
                        opacity:
                          askAiExecutionLoading || !askAiExecutionPrompt.trim()
                            ? 0.6
                            : 1,
                        background:
                          'linear-gradient(135deg, #6d28d9, #a200ff)',
                        color: 'white',
                      }}
                    >
                      {askAiExecutionLoading
                        ? askAiExecutionIsUpdate
                          ? 'Updating…'
                          : 'Generating…'
                        : editExecutionHasDraft
                          ? 'Update'
                          : 'Generate'}
                    </button>
                  </div>
                  {askAiExecutionError && (
                    <p
                      style={{
                        margin: '8px 0 0',
                        fontSize: 11.5,
                        color: '#ef4444',
                      }}
                    >
                      {askAiExecutionError}
                    </p>
                  )}
                </div>
              </div>

              {saveSopError && (
                <p
                  style={{
                    margin: '0 0 10px',
                    fontSize: 11.5,
                    color: '#ef4444',
                    textAlign: 'right',
                  }}
                >
                  {saveSopError}
                </p>
              )}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 10,
                  marginTop: 4,
                }}
              >
                <button
                  onClick={closeEditSopModal}
                  disabled={savingSopCategory}
                  style={{
                    padding: '9px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--border-glass)',
                    background: 'transparent',
                    color: 'var(--text-secondary)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: savingSopCategory ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSopCategory}
                  disabled={savingSopCategory}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    padding: '9px 18px',
                    borderRadius: 8,
                    border: 'none',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: savingSopCategory ? 'not-allowed' : 'pointer',
                    background: 'linear-gradient(135deg, #6d28d9, #a200ff)',
                    color: '#fff',
                    opacity: savingSopCategory ? 0.7 : 1,
                  }}
                >
                  {savingSopCategory && (
                    <FiRefreshCw
                      size={13}
                      style={{ animation: 'spin 1s linear infinite' }}
                    />
                  )}
                  {savingSopCategory ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* ── Share custom category (magic link; the modal portals itself) ── */}
      {sharingSopCategory && (
        <CategoryShareMenuModal
          categoryId={sharingSopCategory.id}
          categoryName={sharingSopCategory.name}
          onClose={() => setSharingSopCategory(null)}
        />
      )}

      {/* ── Delete custom category confirmation (portalled to body) ── */}
      {deletingSopCategory &&
        ReactDOM.createPortal(
          <div
            onClick={closeDeleteSopModal}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10000,
              background: 'rgba(0,0,0,0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
            }}
          >
            <div
              role='dialog'
              aria-modal='true'
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                maxWidth: 420,
                background: 'var(--bg-secondary)',
                borderRadius: 12,
                border: '1px solid var(--border-glass)',
                boxShadow: '0 32px 80px rgba(0,0,0,0.35)',
                padding: 20,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <FiTrash2 size={16} style={{ color: '#ef4444' }} />
                <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                  Delete category?
                </span>
              </div>
              <p
                style={{
                  margin: '0 0 18px',
                  fontSize: 12.5,
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5,
                  wordBreak: 'break-word',
                }}
              >
                <strong style={{ color: 'var(--text-primary)' }}>{deletingSopCategory.name}</strong>{' '}
                will be removed from Command Center and new tasks will no longer be routed to it.
                Existing tasks keep their history, and any share links to it stop working.
              </p>
              {deleteSopError && (
                <p style={{ margin: '0 0 10px', fontSize: 11.5, color: '#ef4444', textAlign: 'right' }}>
                  {deleteSopError}
                </p>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  onClick={closeDeleteSopModal}
                  disabled={deleteSopBusy}
                  style={{
                    padding: '9px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--border-glass)',
                    background: 'transparent',
                    color: 'var(--text-secondary)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: deleteSopBusy ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteSopCategory}
                  disabled={deleteSopBusy}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    padding: '9px 18px',
                    borderRadius: 8,
                    border: 'none',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: deleteSopBusy ? 'not-allowed' : 'pointer',
                    background: '#ef4444',
                    color: '#fff',
                    opacity: deleteSopBusy ? 0.7 : 1,
                  }}
                >
                  {deleteSopBusy && (
                    <FiRefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />
                  )}
                  {deleteSopBusy ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default CommandCenterConfigSidebarPanel;


